import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import {
  CabinetManagerRoleType,
  CabinetManagerServer,
  ICabinetManager,
  IProjectManagerEntity,
  normalizeCabinetManager
} from 'shared/entities/manager';
import { apiUrls } from 'shared/entities/domain';
import { LoadingStage } from 'shared/entities/meta';
import { IBaseManagersStore } from 'shared/entities/store/managersStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import { ManagerExternals } from 'shared/entities/user';

import BaseManagerModel from './BaseManagerModel';

export default class CabinetManagerModel
  extends BaseManagerModel<CabinetManagerModel>
  implements ICabinetManager
{
  cabinetRole: CabinetManagerRoleType | null;

  projects: IProjectManagerEntity[];

  isRoleChangeable: boolean;

  externalIds: ManagerExternals | null;

  constructor({
    cabinetRole,
    id,
    email,
    name,
    managersStore,
    externalIds,
    rootStore,
    projects,
    isRoleChangeable
  }: ICabinetManager & {
    managersStore: IBaseManagersStore<CabinetManagerModel>;
    rootStore: IRootStore;
  }) {
    super({ id, email, managersStore, name, rootStore });
    this.cabinetRole = cabinetRole;
    this.projects = projects;
    this.isRoleChangeable = isRoleChangeable;
    this.externalIds = externalIds;

    makeObservable(this, {
      changeRole: action,
      cabinetRole: observable,

      linkedInTg: computed,
      linkedInVb: computed,
      linkedInVk: computed
    });
  }

  get linkedInTg(): boolean {
    return !!this.externalIds?.tg;
  }

  get linkedInVb(): boolean {
    return !!this.externalIds?.vb;
  }

  get linkedInVk(): boolean {
    return !!this.externalIds?.vk;
  }

  async changeRole({
    role
  }: {
    role: CabinetManagerRoleType;
  }): Promise<LoadingStage> {
    if (this.changingRoleStage === LoadingStage.LOADING) {
      return LoadingStage.ERROR;
    }

    this.changingRoleStage = LoadingStage.LOADING;

    const { isError } = await this.rootStore.networkStore.api(
      apiUrls.MANAGERS_CHANGE_CABINET_ROLE,
      {
        method: 'POST',
        data: {
          _id: this.id,
          cabinet_role: role
        }
      }
    );

    runInAction(() => {
      if (!isError) {
        this.cabinetRole = role;
        this.changingRoleStage = LoadingStage.SUCCESS;
      } else {
        this.changingRoleStage = LoadingStage.ERROR;
      }
    });

    return this.changingRoleStage;
  }

  static fromJson(
    raw: CabinetManagerServer & {
      managersStore: IBaseManagersStore<CabinetManagerModel>;
      rootStore: IRootStore;
    }
  ): CabinetManagerModel {
    return new CabinetManagerModel({
      ...normalizeCabinetManager(raw),
      managersStore: raw.managersStore,
      rootStore: raw.rootStore
    });
  }
}
